/* eslint-disable @typescript-eslint/no-unsafe-argument */
import React, { useState, useEffect, useMemo } from 'react';
import { Spin, Table, Button, Select } from 'antd';
import { useDispatch } from 'react-redux';
import { type AppDispatch } from '../../../redux/store';
import {
  getUnsettledDepositPaymentsAction,
  updateDepositPaymentStatusAction,
} from '../store/depositActions';
import { type DepositPaymentResponse } from '../store/depositTypes';
import dayjs from 'dayjs';
import fmt from 'indian-number-format';

export default function DepositPaymentApprovalIndex(): JSX.Element {
  const dispatch = useDispatch<AppDispatch>();

  const [branchOptions, setBranchOptions] = useState<
    Array<{
      label: string;
      value: string;
    }>
  >([]);

  const [branch, setBranch] = useState<any>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [payments, setPayments] = useState<DepositPaymentResponse[]>([]);

  useEffect(() => {
    const branches = localStorage.getItem('branches');
    if (branches != null) {
      const parsedBranches = JSON.parse(branches);
      const branchOptionTemp: Array<{ label: string; value: string }> =
        parsedBranches.map((branch: { code: string; id: string }) => ({
          label: branch.code,
          value: branch.id,
        }));
      setBranchOptions(branchOptionTemp);
      setBranch(parsedBranches[0].id);
    }
  }, []);

  const getPayments = async (): Promise<void> => {
    setIsLoading(true);
    const res = await getUnsettledDepositPaymentsAction(dispatch, {
      branch,
    });
    setIsLoading(false);
    if (res.meta.requestStatus === 'fulfilled') {
      setPayments(res.payload as DepositPaymentResponse[]);
    }
  };

  const handleUpdateStatus = async (
    id: string,
    status: string
  ): Promise<void> => {
    setIsLoading(true);
    const response = await updateDepositPaymentStatusAction(
      dispatch,
      id,
      status
    );
    setIsLoading(false);
    if (response !== null) {
      void getPayments();
    }
  };

  // Filter payments by status
  const unsettledPayments = useMemo(() => {
    return payments.filter((payment) => payment.status === 'cash_settlement');
  }, [payments]);

  const cashSettledPayments = useMemo(() => {
    return payments.filter((payment) => payment.status === 'cash_settled');
  }, [payments]);

  const rejectedPayments = useMemo(() => {
    return payments.filter((payment) => payment.status === 'rejected');
  }, [payments]);

  const columns = [
    {
      title: 'S.No',
      dataIndex: 'sno',
      render: (_: any, record: DepositPaymentResponse, index: number) =>
        index + 1,
      width: 50,
    },
    {
      title: 'Deposit Number',
      dataIndex: 'depositNumber',
      key: 'depositNumber',
      render: (_: any, record: DepositPaymentResponse) => (
        <p>{record.depositNumber ?? '-'}</p>
      ),
    },
    // {
    //   title: 'Customer Name',
    //   dataIndex: 'customerName',
    //   key: 'customerName',
    //   render: (_: any, record: DepositPaymentResponse) => (
    //     <p>{record.customerName ?? '-'}</p>
    //   ),
    // },
    {
      title: 'Interest Paid',
      dataIndex: 'interestPaid',
      key: 'interestPaid',
      render: (_: any, record: DepositPaymentResponse) => (
        <p>
          Online: {fmt.format(record.onlineInterest ?? 0)} | Offline:{' '}
          {fmt.format(record.offlineInterest ?? 0)} | Total:{' '}
          {fmt.format(record.interestAmount ?? 0)}
          {record.interestDays != null && ` (${record.interestDays} days)`}
        </p>
      ),
    },
    {
      title: 'Principal Paid',
      dataIndex: 'principalPaid',
      key: 'principalPaid',
      render: (_: any, record: DepositPaymentResponse) => {
        const hasPrincipal =
          (record.principal ?? 0) > 0 ||
          (record.onlinePrincipal ?? 0) > 0 ||
          (record.offlinePrincipal ?? 0) > 0;
        return (
          <p>
            {hasPrincipal
              ? `Online: ${fmt.format(
                  record.onlinePrincipal ?? 0
                )} | Offline: ${fmt.format(
                  record.offlinePrincipal ?? 0
                )} | Total: ${fmt.format(record.principal ?? 0)}`
              : '-'}
          </p>
        );
      },
    },
    {
      title: 'Reducing Interest',
      dataIndex: 'reducingInterest',
      key: 'reducingInterest',
      render: (_: any, record: DepositPaymentResponse) => (
        <p>{fmt.format(record.reducingInterest ?? 0)}</p>
      ),
    },
    {
      title: 'Transaction Date',
      dataIndex: 'transactionDate',
      key: 'transactionDate',
      render: (_: any, record: DepositPaymentResponse) => (
        <p>{dayjs(String(record.transactionDate)).format('DD-MM-YYYY')}</p>
      ),
    },
    {
      title: 'Actions',
      dataIndex: 'actions',
      width: 200,
      render: (_: any, record: DepositPaymentResponse) => (
        <div style={{ display: 'flex', gap: 8 }}>
          <p
            style={{
              width: 73,
              height: 34,
              borderRadius: 12,
              backgroundColor: '#4CAF50',
              color: 'white',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={() => {
              void handleUpdateStatus(record._id, 'cash_settled');
            }}
          >
            Accept
          </p>
          <p
            style={{
              width: 73,
              height: 34,
              borderRadius: 12,
              backgroundColor: '#DD1E2D',
              color: 'white',
              fontSize: 14,
              fontWeight: 600,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            onClick={() => {
              void handleUpdateStatus(record._id, 'rejected');
            }}
          >
            Reject
          </p>
        </div>
      ),
    },
  ];

  const completedColumns = [
    {
      title: 'S.No',
      dataIndex: 'sno',
      render: (_: any, record: DepositPaymentResponse, index: number) =>
        index + 1,
      width: 50,
    },
    {
      title: 'Deposit Number',
      dataIndex: 'depositNumber',
      key: 'depositNumber',
      render: (_: any, record: DepositPaymentResponse) => (
        <p>{record.depositNumber ?? '-'}</p>
      ),
    },
    {
      title: 'Customer Name',
      dataIndex: 'customerName',
      key: 'customerName',
      render: (_: any, record: DepositPaymentResponse) => (
        <p>{record.customerName ?? '-'}</p>
      ),
    },
    {
      title: 'Interest Paid',
      dataIndex: 'interestPaid',
      key: 'interestPaid',
      render: (_: any, record: DepositPaymentResponse) => (
        <p>
          Online: {fmt.format(record.onlineInterest ?? 0)} | Offline:{' '}
          {fmt.format(record.offlineInterest ?? 0)} | Total:{' '}
          {fmt.format(record.interestAmount ?? 0)}
          {record.interestDays != null && ` (${record.interestDays} days)`}
        </p>
      ),
    },
    {
      title: 'Principal Paid',
      dataIndex: 'principalPaid',
      key: 'principalPaid',
      render: (_: any, record: DepositPaymentResponse) => {
        const hasPrincipal =
          (record.principal ?? 0) > 0 ||
          (record.onlinePrincipal ?? 0) > 0 ||
          (record.offlinePrincipal ?? 0) > 0;
        return (
          <p>
            {hasPrincipal
              ? `Online: ${fmt.format(
                  record.onlinePrincipal ?? 0
                )} | Offline: ${fmt.format(
                  record.offlinePrincipal ?? 0
                )} | Total: ${fmt.format(record.principal ?? 0)}`
              : '-'}
          </p>
        );
      },
    },
    {
      title: 'Transaction Date',
      dataIndex: 'transactionDate',
      key: 'transactionDate',
      render: (_: any, record: DepositPaymentResponse) => (
        <p>{dayjs(String(record.transactionDate)).format('DD-MM-YYYY')}</p>
      ),
    },
  ];

  return (
    <div className="deposit__table__page">
      <Spin spinning={isLoading} fullscreen />
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          gap: 14,
          alignItems: 'center',
        }}
      >
        <Select
          value={branch}
          onChange={(value: string): void => {
            setBranch(value);
          }}
          style={{ width: 311, height: 56 }}
          placeholder="Select branch"
          options={branchOptions}
          className="expense__input"
        />
        <Button onClick={getPayments} className="search__button">
          <span>Search</span>
        </Button>
      </div>

      {Boolean(unsettledPayments.length) && (
        <div style={{ width: '100%', marginTop: 24 }}>
          <h3 style={{ marginBottom: 16 }}>Unsettled Payments</h3>
          <Table
            dataSource={unsettledPayments}
            columns={columns}
            pagination={false}
            rowKey={(record: DepositPaymentResponse) => record._id}
          />
        </div>
      )}

      {Boolean(cashSettledPayments.length) && (
        <div style={{ width: '100%', marginTop: 24 }}>
          <h3 style={{ marginBottom: 16 }}>Cash Settled Payments</h3>
          <Table
            dataSource={cashSettledPayments}
            columns={completedColumns}
            pagination={false}
            rowKey={(record: DepositPaymentResponse) => record._id}
          />
        </div>
      )}

      {Boolean(rejectedPayments.length) && (
        <div style={{ width: '100%', marginTop: 24 }}>
          <h3 style={{ marginBottom: 16 }}>Rejected Payments</h3>
          <Table
            dataSource={rejectedPayments}
            columns={completedColumns}
            pagination={false}
            rowKey={(record: DepositPaymentResponse) => record._id}
          />
        </div>
      )}
    </div>
  );
}
